import { FC } from 'react';
import { Status } from '../List/Cells/Status.component';
import formatMoney from 'utils/formatMoney';
import { Button } from 'components/atoms/Button';
import { PlusIcon } from 'utils/icons';
import { divideBy1000 } from 'utils/math';
import { Loader } from 'components/atoms/Loader';
import classNames from 'classnames';
import useExtendRevenueProject from 'hooks/Revenues/useExtendRevenueProject';
import { Invoice } from 'types/invoice.types';
import { RevenueProjectStatus } from 'api/v3/revenues';
import { useTranslation } from 'react-i18next';
import { WEBAPP_REVENUE_CREATENEXTINSTALLMENT } from 'utils/amplitude/events/revenues.amplitude';

type Props = {
  revenue: Partial<Invoice>;
  onRevenueClick?: (id: string) => void;
};

const RevenueProjectInvoices: FC<Props> = ({ revenue, onRevenueClick }) => {
  const { t } = useTranslation();

  const extendAPI = useExtendRevenueProject();

  if (!revenue._matchedProject) return null;

  const revenues = revenue._matchedProject.revenues || [];

  const isCompleted =
    revenue._matchedProject.status === RevenueProjectStatus.completed;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm mb-1">
        {t('installment_project.invoices_list')}
      </div>
      {revenues.map((item) => {
        const revenueData = item.revenueId;
        const isCurrentRevenue = revenueData._id === revenue._id;
        return (
          <Button
            asDefault
            disabled={isCurrentRevenue}
            key={revenueData._id}
            title={item.revenueNumber}
            className="flex items-center gap-2 w-full group"
            onClick={() => onRevenueClick?.(revenueData._id)}
          >
            <div
              className={classNames(
                'text-sm font-semibold flex-[2] flex gap-1',
                isCurrentRevenue ? '' : 'text-revenues-700',
              )}
            >
              -
              <div
                className={classNames('truncate', {
                  'group-hover:underline group-focus-visible:underline':
                    !isCurrentRevenue,
                })}
              >
                {item.revenueNumber}
              </div>
              {isCurrentRevenue ? (
                <span className="flex-shrink-0">
                  {'('}
                  {t('installment_project.invoices_list.pointer')}
                  {')'}
                </span>
              ) : (
                ''
              )}
            </div>
            <div className="flex justify-center flex-shrink-0">
              <Status
                status={revenueData.status}
                dueDate={revenueData.dueDate}
              />
            </div>
            <div className="text-sm flex-1 flex-shrink-0 text-right">
              {formatMoney(divideBy1000(item.totalAmountInclVAT))}
            </div>
          </Button>
        );
      })}
      {!isCompleted && (
        <Button
          asDefault
          onClick={() => {
            extendAPI.run(revenue);
          }}
          tracingEvent={WEBAPP_REVENUE_CREATENEXTINSTALLMENT}
          disabled={extendAPI.mutation.isLoading}
          className="mt-1 w-full flex items-center justify-center gap-2 border border-dashed border-primary-200 text-primary-400 text-sm font-semibold py-2.5 rounded-lg hover:text-primary hover:border-primary focus:text-primary focus:border-primary transition-colors"
        >
          {extendAPI.mutation.isLoading ? (
            <Loader className="w-4" />
          ) : (
            <PlusIcon className="w-4" />
          )}
          {t('installment_project.invoices_list.add')}
        </Button>
      )}
    </div>
  );
};

export default RevenueProjectInvoices;
